<template>
  <base-keep-alive
    class="base-virtual-scroll-viewport"
    :style="virtualViewportStyle"
  >
    <base-resize-observer
      v-if="isHeaderVisible"
      :key="HEADER_SLOT_KEY"
      @resize="$emit('header-slot-resize', $event)"
    >
      <div class="base-virtual-scroll-viewport-item">
        <slot name="header" />
      </div>
    </base-resize-observer>

    <base-virtual-scroll-viewport-item
      v-for="(item, vIndex) of visibleItems"
      :key="getItemId(item)"
      :item="item"
      :item-id="getItemId(item)"
      :item-size="getItemSize(item)"
      :real-index="getRealIndex(vIndex)"
      :active-item-id="activeItemId"
      :is-scrolling="isScrolling"
      @activation="handleItemActivation(item)"
      @deactivation="handleItemDeactivation(item)"
      @resize="handleItemResize(item, $event)"
    >
      <template #default>
        <slot
          name="default"
          :index="getRealIndex(vIndex)"
          :data="item"
          :context="context"
          :active-item-id="activeItemId"
        />
      </template>
    </base-virtual-scroll-viewport-item>

    <base-resize-observer
      v-if="isFooterVisible"
      :key="FOOTER_SLOT_KEY"
      @resize="$emit('footer-slot-resize', $event)"
    >
      <div class="base-virtual-scroll-viewport-item">
        <slot name="footer" />
      </div>
    </base-resize-observer>
  </base-keep-alive>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api';

import { BaseKeepAlive, BaseResizeObserver } from '../decorators';
import BaseVirtualScrollViewportItem from './BaseVirtualScrollViewportItem.vue';
import {
  IResizeInfo,
  IVScrollItem,
  IVScrollOffsetTop,
  IVScrollOffsetBottom,
} from '../../types';
import { BASE_VIRTUAL_SCROLL_PROPS } from '../../constants';

/**
 * VScroll viewport renders only subset of items and adds padding for invisible items
 */
export default defineComponent({
  name: 'BaseVirtualScrollViewport',
  components: {
    BaseKeepAlive,
    BaseResizeObserver,
    BaseVirtualScrollViewportItem,
  },
  props: {
    ...BASE_VIRTUAL_SCROLL_PROPS,
    offsetTop: {
      type: Object as PropType<IVScrollOffsetTop>,
      default: undefined,
    },
    offsetBottom: {
      type: Object as PropType<IVScrollOffsetBottom>,
      default: undefined,
    },
    visibleItems: {
      type: Array as PropType<IVScrollItem[]>,
      required: true,
    },
    itemSizesById: {
      type: Object as PropType<Record<number | string, number>>,
      required: true,
    },
  },
  emits: ['item-resize', 'header-slot-resize', 'footer-slot-resize'],
  setup() {
    return {
      HEADER_SLOT_KEY: 'base-virtual-scroll-header',
      FOOTER_SLOT_KEY: 'base-virtual-scroll-footer',
    };
  },
  computed: {
    isHeaderVisible(): boolean {
      return this.offsetTop.isHeaderSlotVisible && !!this.$slots.header;
    },

    isFooterVisible(): boolean {
      return this.offsetBottom.isFooterSlotVisible && !!this.$slots.footer;
    },

    virtualViewportStyle(): Record<string, string> {
      const paddingTop = this.offsetTop.offsetTopDistanceBuffer;
      const paddingBottom = this.offsetBottom.offsetBottomDistanceBuffer;
      return {
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
      };
    },
  },
  methods: {
    getItemId(item: IVScrollItem): string | number {
      return item[this.dataKey];
    },

    getItemSize(item: IVScrollItem): number {
      const itemId = this.getItemId(item);
      return this.itemSizesById[itemId] ?? this.defaultItemSize;
    },

    getRealIndex(index: number): number {
      return index + this.offsetTop.offsetTopItemsBuffer;
    },

    handleItemResize(item: IVScrollItem, resize: IResizeInfo): void {
      this.$emit('item-resize', item, resize);
    },

    handleItemActivation(item: IVScrollItem): void {
      this.$emit('item-activation', item);
    },

    handleItemDeactivation(item: IVScrollItem): void {
      this.$emit('item-deactivation', item);
    },
  },
});
</script>

<style lang="scss" scoped>
.base-virtual-scroll-viewport {
  display: flex;
  flex-direction: column;
}
</style>
